import { Button, MenuItem, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { InternalAddCustomerUserRelationModal } from "features/internal/provisioning/modals/InternalAddCustomerUserRelationModal";
import {
  getAdminFundFamilyTableDataRetrieveQueryKey,
  useAdminFundFamilyTableDataRetrieveSuspense,
} from "gen/endpoints/admin-fund-family-table-data/admin-fund-family-table-data";
import {
  getCustomerRetrieveQueryKey,
  useCustomerDestroy,
  useCustomerRetrieveSuspense,
  useCustomerUpdate,
  useInternalCustomerUserRelationDestroy,
} from "gen/endpoints/customer/customer";
import { useFundFamilyDestroy } from "gen/endpoints/fund-family/fund-family";
import {
  getInternalProvisioningCustomersTableDataRetrieveQueryKey,
  getInternalProvisioningCustomerUserRelationsTableDataRetrieveQueryKey,
  getInternalUnrelatedUserListQueryKey,
  useInternalProvisioningCustomerUserRelationsTableDataRetrieveSuspense,
} from "gen/endpoints/internal/internal";
import { CustomerData } from "gen/model/customerData";
import { CustomerDataRequest } from "gen/model/customerDataRequest";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { customerUpdateBody } from "gen/schemas/customer/customer";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { FormBooleanToggleButtonGroup } from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { FormDrawer } from "shared/components/form/FormDrawer";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { RenderRowActionsFn } from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const InternalProvisioningCustomerPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["common", "enums", "internal", "fundFamily"]);
  const { customerId } = useRequiredStringParams();
  const { showToast, showErrorToast } = useToast();

  const {
    value: isCustomerUserRelationModalOpen,
    setTrue: openCustomerUserRelationModal,
    setFalse: closeCustomerUserRelationModal,
  } = useBoolean();

  const {
    isOpen: isEditDrawerOpen,
    onOpen: onEditDrawerOpen,
    onClose: onEditDrawerClose,
  } = useDisclosure();

  const { data: customer } = useCustomerRetrieveSuspense(customerId);
  const navigate = useNavigate();

  const { data: fundFamilyTableData } =
    useAdminFundFamilyTableDataRetrieveSuspense({ customer_id: customerId });

  const { data: userTableData } =
    useInternalProvisioningCustomerUserRelationsTableDataRetrieveSuspense(
      customerId,
    );

  const { mutateAsync: destroyCustomer } = useCustomerDestroy({
    mutation: {
      onSuccess: async () => {
        await invalidateQueryKeys(
          queryClient,
          getInternalProvisioningCustomersTableDataRetrieveQueryKey(),
        );
        showToast({
          variant: "warning",
          message: t("common:deleted"),
        });
        navigate(
          rootRoute({}).internal({}).provisioning({}).customersTab({}).$,
        );
      },
      onError: (err) => showErrorToast(err),
    },
  });

  const { mutateAsync: destroyFundFamily } = useFundFamilyDestroy({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getAdminFundFamilyTableDataRetrieveQueryKey(),
        });
        showToast({
          variant: "warning",
          message: t("common:deleted"),
        });
      },
    },
  });

  const { mutateAsync: removeUserFromCustomer } =
    useInternalCustomerUserRelationDestroy({
      mutation: {
        onSuccess: async () => {
          await Promise.allSettled([
            queryClient.invalidateQueries({
              queryKey:
                getInternalProvisioningCustomerUserRelationsTableDataRetrieveQueryKey(
                  customerId,
                ),
            }),
            queryClient.invalidateQueries({
              queryKey: getInternalUnrelatedUserListQueryKey({
                customer_id: customerId,
              }),
            }),
          ]);
          showToast({
            variant: "warning",
            message: t("internal:modal.removeUser.success"),
          });
        },
      },
    });

  const fundFamilyTableRenderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => ({
      type: "menu",
      actions: (
        <Restricted permission={PermissionTypeEnum.service}>
          <MenuItem
            color="error.500"
            _hover={{ bg: "error.100" }}
            onClick={async () => {
              if (row.data?.metadata?.type === "id_row") {
                await destroyFundFamily({
                  fundFamilyId: row.data.metadata.id,
                  params: { customer_id: customerId },
                });
              }
            }}
          >
            {t("fundFamily:delete.title")}
          </MenuItem>
        </Restricted>
      ),
    }),
    [t, destroyFundFamily, customerId],
  );

  const userTableRenderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (metadata?.type !== "id_row") {
        return undefined;
      }
      return {
        type: "menu",
        actions: (
          <Restricted permission={PermissionTypeEnum.service}>
            <MenuItem
              color="error.500"
              _hover={{ bg: "error.100" }}
              onClick={async () =>
                removeUserFromCustomer({ customerId, userId: metadata.id })
              }
            >
              {t("internal:modal.removeUser.title")}
            </MenuItem>
          </Restricted>
        ),
      };
    },
    [t, removeUserFromCustomer, customerId],
  );

  const onEditSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: getCustomerRetrieveQueryKey(customerId),
    });
    showToast({
      variant: "success",
      message: t("internal:customerPage.edit.success"),
    });
  };

  return (
    <PageContent>
      <PageHeader
        title={customer.name}
        badge={{
          type: customer.enable_delete_protection ? "success" : "error",
          text: customer.enable_delete_protection
            ? t("internal:customerPage.isDeleteProtectionEnabled")
            : t("internal:customerPage.isDeleteProtectionDisabled"),
        }}
        actions={
          <Restricted permission={PermissionTypeEnum.service}>
            <Button
              size="md"
              colorScheme="destructive"
              onClick={() => destroyCustomer({ id: customerId })}
            >
              {t("common:delete")}
            </Button>
          </Restricted>
        }
        backButton={{
          title: t("common:back"),
          path: rootRoute({}).internal({}).provisioning({}).customersTab({}).$,
        }}
      />
      <PageBody>
        <ContentBlockGroup>
          <ContentBlock>
            <ContentBlockHeader>
              <ContentBlockTitle>{t("common:details")}</ContentBlockTitle>
              <Restricted permission={PermissionTypeEnum.service}>
                <ContentBlockActions>
                  <ContentBlockMenu>
                    <MenuItem onClick={onEditDrawerOpen}>
                      {t("common:edit")}
                    </MenuItem>
                  </ContentBlockMenu>
                </ContentBlockActions>
              </Restricted>
            </ContentBlockHeader>
            <ContentBlockBody variant="descriptions">
              <Descriptions>
                <Descriptions.Item label={t("common:name")}>
                  {customer.name}
                </Descriptions.Item>
                <Descriptions.Item label={t("internal:customerPage.domain")}>
                  {customer.domain}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("internal:customerPage.isDeleteProtectionEnabled")}
                >
                  {customer.enable_delete_protection}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("internal:customerPage.internalID")}
                >
                  {customer.id}
                </Descriptions.Item>

                <Descriptions.Item label={t("internal:customerPage.state")}>
                  {t(
                    `enums:GenericVersionStateMachineFieldsEnum.${customer.state}`,
                  )}
                </Descriptions.Item>
              </Descriptions>
            </ContentBlockBody>
          </ContentBlock>
        </ContentBlockGroup>
        {isEditDrawerOpen && (
          <InternalProvisioningCustomerEditDrawer
            customer={customer}
            onSuccess={onEditSuccess}
            onCloseComplete={onEditDrawerClose}
          />
        )}
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:fundFamilies")}</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={fundFamilyTableData}
              renderRowActions={fundFamilyTableRenderRowActions}
              fillWidth={true}
              disablePinnedHeaderColumns={true}
            />
          </TableBoxBody>
        </TableBox>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:users")}</TableBoxTitle>
            <DataTableBoxActions>
              <Button size="xs" onClick={openCustomerUserRelationModal}>
                {t("internal:modal.linkUser.title")}
              </Button>
            </DataTableBoxActions>
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={userTableData}
              renderRowActions={userTableRenderRowActions}
              fillWidth={true}
              disablePinnedHeaderColumns={true}
            />
          </TableBoxBody>
        </TableBox>
        {isCustomerUserRelationModalOpen && (
          <InternalAddCustomerUserRelationModal
            customerId={customerId}
            onCloseComplete={closeCustomerUserRelationModal}
          />
        )}
      </PageBody>
    </PageContent>
  );
};

const InternalProvisioningCustomerEditDrawer: React.FC<{
  customer: CustomerData;
  onSuccess: () => void;
  onCloseComplete: () => void;
}> = ({ customer, onSuccess, onCloseComplete }) => {
  const { t } = useTranslation(["internal"]);
  const { mutateAsync: updateCustomer } = useCustomerUpdate({
    mutation: {
      onSuccess,
    },
  });
  const form = useForm<CustomerDataRequest>({
    resolver: zodResolver(
      customerUpdateBody.pick({ enable_delete_protection: true }),
    ),
    defaultValues: customer,
  });
  const onSubmit = async (data: CustomerDataRequest) =>
    await updateCustomer({
      id: customer.id!,
      data,
    });

  return (
    <FormDrawer
      title={t("internal:customerPage.edit.title")}
      formProps={{
        form,
        onSubmit,
      }}
      onCloseComplete={onCloseComplete}
    >
      <FormBooleanToggleButtonGroup
        title={t("internal:customerPage.isDeleteProtectionEnabled")}
        name="enable_delete_protection"
        trueText={t("common:yes")}
        falseText={t("common:no")}
      />
    </FormDrawer>
  );
};
